import { Fragment, useEffect, useState } from "react";
import { Ticket, Check, X, Ban, ChevronDown, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const STATUS = {
  confirmee: { label: "Confirmée", classes: "bg-emerald-50 text-emerald-600", dot: "bg-emerald-500" },
  refusee: { label: "Refusée", classes: "bg-red-50 text-red-500", dot: "bg-red-500" },
  annulee: { label: "Annulée", classes: "bg-slate-100 text-slate-500", dot: "bg-slate-400" },
};

const FILTERS = [
  { key: "toutes", label: "Toutes" },
  { key: "confirmee", label: "Confirmées" },
  { key: "refusee", label: "Refusées" },
  { key: "annulee", label: "Annulées" },
];

export const ReservationsView = () => {
  const { t, lang } = useLanguage();
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("toutes");
  const [busy, setBusy] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);

  const load = () => api.get("/pro/reservations").then((r) => setRows(r.data)).catch(() => setRows([]));

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (r, status) => {
    setBusy(r.id);
    try {
      await api.patch(`/pro/reservations/${r.id}`, { status });
      toast.success(status === "confirmee" ? "Réservation confirmée" : status === "refusee" ? "Réservation refusée" : "Réservation annulée");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const visible = (rows || []).filter((r) => filter === "toutes" || r.status === filter);

  return (
    <div className="mt-6 rounded-3xl border border-slate-100 bg-white p-5 shadow-sm sm:p-6" data-testid="pro-reservations-view">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
          <Ticket className="h-4 w-4 text-[#2563EB]" />
          Toutes les réservations
        </h2>
        <div className="flex gap-1.5" data-testid="reservations-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              data-testid={`reservations-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`rounded-full px-3 py-1.5 text-[11px] font-semibold transition ${
                filter === f.key ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        {rows === null ? (
          <div className="h-24 animate-pulse rounded-2xl bg-[#F8FAFC]" />
        ) : visible.length === 0 ? (
          <p data-testid="reservations-empty" className="rounded-2xl bg-[#F8FAFC] p-6 text-center text-xs text-slate-400">
            {rows.length === 0
              ? "Aucune réservation pour l'instant. Publie un événement pour en recevoir !"
              : "Aucune réservation dans cette catégorie."}
          </p>
        ) : (
          <table className="w-full min-w-[720px] text-left text-xs">
            <thead>
              <tr className="text-slate-400">
                <th className="pb-2 pr-3 font-medium">{t("Client")}</th>
                <th className="pb-2 pr-3 font-medium">{t("Événement")}</th>
                <th className="pb-2 pr-3 font-medium">{t("Date")}</th>
                <th className="pb-2 pr-3 font-medium">{t("Personnes")}</th>
                <th className="pb-2 pr-3 font-medium">{t("Statut")}</th>
                <th className="pb-2 font-medium">{t("Actions")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const s = STATUS[r.status] || STATUS.confirmee;
                return (
                  <Fragment key={r.id}>
                    <tr data-testid={`pro-reservation-row-${i}`} className="border-t border-slate-50">
                      <td className="py-2.5 pr-3">
                        <span className="flex items-center gap-2">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#EFF6FF] text-[10px] font-bold text-[#2563EB]">
                            {r.client[0]}
                          </span>
                          <span className="font-semibold text-slate-800">{r.client}</span>
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-slate-500">{r.event_title}</td>
                      <td className="py-2.5 pr-3 text-slate-500">{r.date} • {r.time}</td>
                      <td className="py-2.5 pr-3 text-slate-500">{r.guests}</td>
                      <td className="py-2.5 pr-3">
                        <span className="flex w-fit items-center gap-1.5">
                          <span data-testid={`reservation-status-${i}`} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${s.classes}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
                            {s.label}
                          </span>
                          {r.paid && (
                            <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-semibold text-[#2563EB]">{t("Payée")}</span>
                          )}
                        </span>
                      </td>
                      <td className="py-2.5">
                        <span className="flex items-center gap-1">
                          {r.status !== "confirmee" && (
                            <button type="button" data-testid={`reservation-confirm-${i}`} onClick={() => setStatus(r, "confirmee")} disabled={busy === r.id} aria-label={t("Confirmer")} title={t("Confirmer")} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-emerald-300 hover:text-emerald-600 disabled:opacity-50">
                              <Check className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {r.status !== "refusee" && (
                            <button type="button" data-testid={`reservation-refuse-${i}`} onClick={() => setStatus(r, "refusee")} disabled={busy === r.id} aria-label={t("Refuser")} title={t("Refuser")} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-red-300 hover:text-red-500 disabled:opacity-50">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {r.status !== "annulee" && (
                            <button type="button" data-testid={`reservation-cancel-${i}`} onClick={() => setStatus(r, "annulee")} disabled={busy === r.id} aria-label={t("Annuler")} title={t("Annuler")} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-amber-300 hover:text-amber-500 disabled:opacity-50">
                              <Ban className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            data-testid={`reservation-details-${i}`}
                            onClick={() => setDetailsFor(detailsFor === r.id ? null : r.id)}
                            aria-label={t("Détails")}
                            title={t("Détails")}
                            className={`flex h-7 w-7 items-center justify-center rounded-full border transition ${detailsFor === r.id ? "border-[#2563EB] text-[#2563EB]" : "border-slate-200 text-slate-500 hover:border-blue-300 hover:text-[#2563EB]"}`}
                          >
                            <ChevronDown className={`h-3.5 w-3.5 transition ${detailsFor === r.id ? "rotate-180" : ""}`} />
                          </button>
                        </span>
                      </td>
                    </tr>
                    {detailsFor === r.id && (
                      <tr data-testid={`reservation-details-panel-${i}`} className="border-t border-slate-50">
                        <td colSpan={6} className="bg-[#F8FAFC] px-4 py-3">
                          <div className="flex flex-wrap gap-x-8 gap-y-1.5 text-xs text-slate-600">
                            <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-400" />{r.client_email || "Courriel indisponible"}</span>
                            <span>Réservé {r.created}</span>
                            <span>{r.guests} personne(s)</span>
                            <span>{r.paid ? "Payée en ligne" : "Paiement sur place ou gratuit"}</span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
